import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { X, Send, Bot, User, ArrowLeft } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { z } from "zod";

interface Message {
  role: "user" | "assistant";
  content: string;
}

interface ChatbotProps {
  onClose: () => void;
  onBack: () => void;
}

const messageSchema = z.object({
  content: z.string().trim().min(1, "Message cannot be empty").max(5000, "Message is too long (max 5000 characters)"),
});

/**
 * Fact correction sent with every request.
 *
 * The deployed `chat` edge function still carries an older system prompt that names
 * PT Berca Hardayaperkasa as the current employer. Editing that function needs a paid
 * Lovable plan, so the correction is supplied from here instead: the function builds its
 * payload as [its own system prompt, ...these messages], which puts this block after the
 * stale one where it takes precedence.
 *
 * Delete this once supabase/functions/chat/index.ts can be redeployed — that file already
 * carries the correct text.
 */
const PROFILE_CORRECTION: Message = {
  role: "system",
  content: [
    "IMPORTANT — the following supersedes any conflicting employment details stated earlier in this conversation.",
    "",
    "CURRENT ROLE (since March 2026): DevOps Engineer at Apple Developer Institute, Indonesia — the DevOps track, run in partnership with S-Quantum Engine. Responsibilities:",
    "- Designed and maintained CI/CD pipelines (Bitbucket Pipelines, self-hosted runners) across dev, staging, and production environments, automating release flows that previously required fully manual deployment.",
    "- Provisioned 20+ multi-environment AWS stacks as code with Terraform (15 reusable modules) via GitOps (Atlantis), with tfsec, Checkov, and Infracost scanning every pull request before merge.",
    "- Migrated the platform to Kubernetes on Amazon EKS, giving each workload its own identity with EKS Pod Identity and IRSA, publishing container images to ECR, and keeping services available through an ALB Ingress on ACM, PodDisruptionBudgets, and topology spread constraints across availability zones.",
    "- Built immutable, artifact-based deployments using Packer AMI baking and 12 Ansible roles tested with Molecule, cutting environment provisioning to a single automated pipeline run.",
    "- Secured infrastructure through Linux hardening (sshd, ufw, fail2ban), self-managed OpenVPN, and least-privilege security groups across 10+ EC2 servers in 3 environments.",
    "- Automated operational workflows with a Telegram ChatOps bot (deploy status, health checks, gated infra actions), eliminating repetitive manual ops checks.",
    "- Set up and manage iOS CI/CD with Xcode Cloud, automating Swift app builds, testing, and TestFlight distribution to speed up release cycles.",
    "",
    "PREVIOUS ROLE (April 2023 – April 2026): Technical Consultant at PT Berca Hardayaperkasa. This is NOT his current job. Do not describe it as current, present, or ongoing.",
    "",
    "If asked where he works now, answer: Apple Developer Institute.",
  ].join("\n"),
};

const Chatbot = ({ onClose, onBack }: ChatbotProps) => {
  const { t } = useLanguage();
  const { session } = useAuth();
  const [messages, setMessages] = useState<Message[]>([
    {
      role: "assistant",
      content: t.chatbot.welcome,
    },
  ]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const scrollAreaRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Use requestAnimationFrame to avoid forced reflow
    const scrollToBottom = () => {
      if (scrollAreaRef.current) {
        scrollAreaRef.current.scrollTop = scrollAreaRef.current.scrollHeight;
      }
    };
    
    requestAnimationFrame(scrollToBottom);
  }, [messages]);

  const handleSend = async () => {
    if (!input.trim() || isLoading) return;

    // Validate input
    const validationResult = messageSchema.safeParse({ content: input });
    
    if (!validationResult.success) {
      const firstError = validationResult.error.errors[0];
      toast.error(firstError.message);
      return;
    }

    const userMessage: Message = { role: "user", content: validationResult.data.content };
    setMessages((prev) => [...prev, userMessage]);
    setInput("");
    setIsLoading(true);

    try {
      const headers: Record<string, string> = {};
      if (session?.access_token) {
        headers.Authorization = `Bearer ${session.access_token}`;
      }

      const { data, error } = await supabase.functions.invoke("chat", {
        // The function caps the array at 20 entries; keep the newest history and always
        // leave room for the correction block.
        body: {
          messages: [PROFILE_CORRECTION, ...messages.slice(-18), userMessage],
        },
        headers
      });

      if (error) throw error;

      const assistantMessage: Message = {
        role: "assistant",
        content: data.response,
      };
      setMessages((prev) => [...prev, assistantMessage]);
    } catch (error) {
      console.error("Chat error:", error);
      toast.error(t.chatbot.error);
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: t.chatbot.error,
        },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed bottom-6 right-6 z-50 w-96 max-w-[calc(100vw-3rem)]">
      <Card className="shadow-xl animate-fade-up h-[600px] flex flex-col">
        <CardHeader className="flex flex-row items-center justify-between pb-3 border-b shrink-0">
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={onBack}
              aria-label="Go back to portfolio selector"
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <Bot className="h-5 w-5 text-primary" />
            <CardTitle className="text-lg">{t.chatbot.title}</CardTitle>
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={onClose}
            aria-label="Close chatbot"
          >
            <X className="h-4 w-4" />
          </Button>
        </CardHeader>
        <CardContent className="flex-1 flex flex-col p-0 min-h-0 overflow-hidden">
          <ScrollArea className="flex-1 p-4 overflow-y-auto" ref={scrollAreaRef}>
            <div className="space-y-4">
              {messages.map((message, index) => (
                <div
                  key={index}
                  className={`flex gap-3 ${
                    message.role === "user" ? "flex-row-reverse" : ""
                  }`}
                >
                  <div
                    className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${
                      message.role === "user"
                        ? "bg-primary text-primary-foreground"
                        : "bg-muted"
                    }`}
                  >
                    {message.role === "user" ? (
                      <User className="h-4 w-4" />
                    ) : (
                      <Bot className="h-4 w-4" />
                    )}
                  </div>
                  <div
                    className={`flex-1 space-y-2 ${
                      message.role === "user" ? "text-right" : ""
                    }`}
                  >
                    <div
                      className={`inline-block rounded-lg px-4 py-2 ${
                        message.role === "user"
                          ? "bg-primary text-primary-foreground"
                          : "bg-muted"
                      }`}
                    >
                      <p className="text-sm whitespace-pre-wrap">
                        {message.content}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
              {isLoading && (
                <div className="flex gap-3">
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-muted">
                    <Bot className="h-4 w-4" />
                  </div>
                  <div className="flex-1 space-y-2">
                    <div className="inline-block rounded-lg px-4 py-2 bg-muted">
                      <div className="flex gap-1">
                        <div className="h-2 w-2 rounded-full bg-muted-foreground animate-bounce" />
                        <div className="h-2 w-2 rounded-full bg-muted-foreground animate-bounce [animation-delay:0.2s]" />
                        <div className="h-2 w-2 rounded-full bg-muted-foreground animate-bounce [animation-delay:0.4s]" />
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </ScrollArea>
          <div className="border-t p-4 shrink-0">
            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleSend();
              }}
              className="flex gap-2"
            >
              <Input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder={t.chatbot.placeholder}
                disabled={isLoading}
                className="flex-1"
              />
              <Button type="submit" size="icon" disabled={isLoading || !input.trim()} aria-label="Send message">
                <Send className="h-4 w-4" />
              </Button>
            </form>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Chatbot;
